/**
 * Client-side helpers for the head-to-head challenge feature.
 *
 * Flow:
 *  1. Challenger finishes a run → /api/challenges (POST) → gets code.
 *  2. Challenger shares /challenge/<code>.
 *  3. Accepter opens the URL → page launches /<game>?challenge=<code>.
 *  4. Game reads ?challenge=<code>, fetches details, uses the seed.
 *  5. Accepter finishes their ranked run → POST /api/challenges/<code>/accept
 *     with their {handle, score, run_id}.
 *  6. Result card shows the head-to-head winner.
 */

export interface ChallengeView {
  code: string;
  game: string;
  seed: number;
  challenger: { handle: string; score: number };
  accepter: { handle: string; score: number } | null;
  created_at: string;
  accepted_at: string | null;
  expires_at: string;
  expired: boolean;
  winner: string | null;
}

export interface CreateChallengeInput {
  game: string;
  seed: number;
  handle: string;
  score: number;
  runId?: string;
}

export async function createChallenge(input: CreateChallengeInput): Promise<{ code: string; expires_at: string } | null> {
  try {
    const res = await fetch("/api/challenges", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        game: input.game,
        seed: input.seed,
        handle: input.handle,
        score: input.score,
        run_id: input.runId,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

export async function fetchChallenge(code: string): Promise<ChallengeView | null> {
  try {
    const res = await fetch(`/api/challenges/${encodeURIComponent(code)}`);
    if (!res.ok) return null;
    return (await res.json()) as ChallengeView;
  } catch { return null; }
}

export async function acceptChallenge(code: string, handle: string, score: number, runId?: string)
  : Promise<{ winner: string; challenger: { handle: string; score: number }; accepter: { handle: string; score: number }; already_accepted?: boolean } | null> {
  try {
    const res = await fetch(`/api/challenges/${encodeURIComponent(code)}/accept`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, score, run_id: runId }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** Return the current ?challenge=CODE URL param, if any. */
export function getActiveChallengeCode(): string | null {
  try {
    const code = new URL(window.location.href).searchParams.get("challenge");
    if (!code || !/^[A-Z2-9]{5}$/i.test(code)) return null;
    return code.toUpperCase();
  } catch { return null; }
}
